/**
 * PRD.330 — Generic report executor binding (V1: PowerSync / local SQLite).
 *
 * Runs a report's compiled rows / summary / facet QuerySpecs for ONE applied
 * `ReportQuery` through `usePowerSyncQuery`, and assembles a Phase 1
 * `ReportResult`. Report-specific SQL lives in @helios/data-access; the
 * consumer passes its spec builders in. A future REST executor can expose the
 * same (definition, query) → ReportResult shape.
 */
import { useMemo, useState } from 'react';
import {
  localDayRangeToUtcBounds,
  type ReportDefinition,
  type ReportQuery,
  type ReportResult,
  type ReportUtcBounds,
} from '@helios/domain';
import { usePowerSyncQuery } from '../adapters/powersync';
import {
  assembleReportResult,
  type ReportQueryPart,
} from '../components/reports/report-view-logic';

export interface ReportSpecLike {
  key: readonly unknown[];
  sql: string;
  params: unknown[];
  watch: string[];
}

export type ReportSpecBuilder<C extends string> = (
  query: ReportQuery<C>,
  bounds: ReportUtcBounds,
) => ReportSpecLike;

export interface ReportQueryBindings<C extends string> {
  rows: ReportSpecBuilder<C>;
  summary: ReportSpecBuilder<C>;
  /** One builder per `definition.facetColumns` entry. */
  facets?: Partial<Record<C, ReportSpecBuilder<C>>>;
  /** PRD.330 Phase 4 — one grouped builder per `definition.visualizations` id. */
  visualizations?: Readonly<Record<string, ReportSpecBuilder<C>>>;
}

/** Inert spec used when compilation fails, so hook call order never changes. */
const NOOP_SPEC: ReportSpecLike = {
  key: ['reports', 'noop'],
  sql: 'SELECT 1 WHERE 0',
  params: [],
  watch: [],
};

/**
 * `bindings` should be a module-level constant (or memoized) so the compiled
 * specs only change when the applied query changes.
 */
export function useReportQuery<Row, C extends string>(
  definition: ReportDefinition<C>,
  query: ReportQuery<C>,
  bindings: ReportQueryBindings<C>,
  labelFor: (columnId: C, value: string) => string = (_c, v) => v,
): ReportResult<Row, C> & { retry: () => void } {
  const [nonce, setNonce] = useState(0);

  const compiled = useMemo(() => {
    try {
      const bounds = localDayRangeToUtcBounds(query.dateRange);
      const withNonce = (spec: ReportSpecLike): ReportSpecLike => ({
        ...spec,
        key: [...spec.key, `retry:${nonce}`],
      });
      return {
        failed: false,
        rows: withNonce(bindings.rows(query, bounds)),
        summary: withNonce(bindings.summary(query, bounds)),
        facets: definition.facetColumns.map((columnId) => {
          const build = bindings.facets?.[columnId];
          return { columnId, spec: build ? withNonce(build(query, bounds)) : NOOP_SPEC };
        }),
        visualizations: (definition.visualizations ?? []).map((viz) => {
          const build = bindings.visualizations?.[viz.id];
          return { id: viz.id, spec: build ? withNonce(build(query, bounds)) : NOOP_SPEC };
        }),
      };
    } catch {
      // Compile errors mean an invalid query slipped past the controller;
      // surface the error state instead of throwing during render.
      return {
        failed: true,
        rows: NOOP_SPEC,
        summary: NOOP_SPEC,
        facets: definition.facetColumns.map((columnId) => ({ columnId, spec: NOOP_SPEC })),
        visualizations: (definition.visualizations ?? []).map((viz) => ({
          id: viz.id,
          spec: NOOP_SPEC,
        })),
      };
    }
  }, [definition, query, bindings, nonce]);

  const rows = usePowerSyncQuery<Row>(compiled.rows);
  const summary = usePowerSyncQuery<Record<string, unknown>>(compiled.summary);
  // `definition.facetColumns` is a module-level constant, so this loop makes
  // the same number of hook calls on every render.
  const facetParts = compiled.facets.map(({ columnId, spec }) => ({
    columnId,
    part: usePowerSyncQuery<{ value: unknown; count: unknown }>(spec) as ReportQueryPart<{
      value: unknown;
      count: unknown;
    }>,
  }));

  // Same stable-length rule: `definition.visualizations` is a module constant.
  const visualizationParts = compiled.visualizations.map(({ id, spec }) => ({
    id,
    part: usePowerSyncQuery<Record<string, unknown>>(spec) as ReportQueryPart<
      Record<string, unknown>
    >,
  }));

  const result = assembleReportResult<Row, C>({
    definition,
    rows: rows as ReportQueryPart<Row>,
    summary: summary as ReportQueryPart<Record<string, unknown>>,
    facets: facetParts,
    labelFor,
    compileFailed: compiled.failed,
    query,
    visualizations: visualizationParts,
  });

  return { ...result, retry: () => setNonce((n) => n + 1) };
}
